// stepper-grid.component.ts
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

interface Step {
  id: number;
  title: string;
  route: string;
  status: 'completed' | 'active' | 'pending';
}

@Component({
  selector: 'app-stepper-grid',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stepper-grid.component.html',
  styleUrl: './stepper-grid.component.css'
})
export class StepperGridComponent implements OnInit {
  private router = inject(Router);
  
  // Completion route where all steps should appear as completed
  completionRoute = 'manage-account/create-approve';
  
  steps = signal<Step[]>([
    { id: 1, title: 'Create Company', route: 'manage-account/create-company', status: 'pending' },
    { id: 2, title: 'Create User', route: 'manage-account/create-user', status: 'pending' },
    { id: 3, title: 'Create Profile', route: 'manage-account/create-site', status: 'pending' },
    { id: 4, title: 'Create Platform', route: 'manage-account/create-platform', status: 'pending' },
    { id: 5, title: 'Create Feed', route: 'manage-account/create-feed', status: 'pending' },
    { id: 6, title: 'Create Video Template', route: 'manage-account/create-video-template', status: 'pending' },
    { id: 7, title: 'Create Pricing Plan', route: 'manage-account/create-pricing-plan', status: 'pending' }
  ]);

  ngOnInit() {
    // Subscribe to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateStepStatus();
    });

    // Initial update
    this.updateStepStatus();
  }

  updateStepStatus() {
    const currentRoute = this.router.url;
    
    // Check if we're on the completion route
    if (currentRoute.includes(this.completionRoute) || currentRoute === this.completionRoute) {
      // All steps are completed on the completion route
      this.steps.update(prevSteps => 
        prevSteps.map(step => ({
          ...step,
          status: 'completed' as const
        }))
      );
      return;
    }
    
    // Regular route logic
    const currentStepIndex = this.steps().findIndex(step => 
      currentRoute.includes(step.route) || step.route === currentRoute
    );

    // Update steps with new status
    this.steps.update(prevSteps => 
      prevSteps.map((step, index) => {
        if (currentStepIndex === -1) {
          // No matching route found
          return { ...step, status: 'pending' as const };
        } else if (index === currentStepIndex) {
          return { ...step, status: 'active' as const };
        } else if (index < currentStepIndex) {
          return { ...step, status: 'completed' as const };
        } else {
          return { ...step, status: 'pending' as const };
        }
      })
    );
  }

  navigateToStep(step: Step) {
    // Allow navigation from completion route as well
    if (step.status === 'completed' || step.status === 'active') {
      this.router.navigate([step.route]);
    }
  }

  // Helper method to get step CSS classes
  getStepClasses(status: 'completed' | 'active' | 'pending') {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white';
      case 'active':
        return 'border-2 border-blue-600 bg-white text-blue-600';
      case 'pending':
        return 'border-2 border-gray-300 bg-white text-gray-400';
      default:
        return 'border-2 border-gray-300 bg-white text-gray-400';
    }
  }

  getTitleClasses(status: 'completed' | 'active' | 'pending') {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'active':
        return 'text-blue-600';
      case 'pending':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  }

  getConnectorClasses(status: 'completed' | 'active' | 'pending') {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'active':
        return 'bg-blue-600';
      case 'pending':
        return 'bg-gray-300';
      default:
        return 'bg-gray-300';
    }
  }
}
